import { ReactNode, useState } from "react";
import { Tabs, Select, DatePicker, Table, Segmented } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useProjects } from "../../queries/projects";
import { useClients } from "../../queries/clients";
import { useUsers } from "../../queries/users";
import {
  useReportByProject, useReportByClient, useReportByUser,
  useReportTimeline, useReportVelocity,
} from "../../queries/reports";
import { ProjectProps, ClientProps, SessionUser } from "../../types/interfaces";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell } from "../../components/ui/PageShell";
import { EmptyState } from "../../components/ui/States";
import styles from "./reports.module.css";

const { RangePicker } = DatePicker;

const defaultRange = (): [Dayjs, Dayjs] => [dayjs().startOf("month"), dayjs().endOf("day")];
const hrs = (m: number) => (m / 60).toFixed(1);

const Filters = ({ children }: { children: ReactNode }) => (
  <div className={styles.filters}>
    <span className={styles.filterLabel}>Scope</span>
    {children}
  </div>
);

const Stats = ({ items }: { items: { label: string; value: string; note?: string }[] }) => (
  <div className={styles.stats}>
    {items.map((s) => (
      <div className={styles.stat} key={s.label}>
        <div className="q-kicker">{s.label}</div>
        <div className={styles.statValue}>{s.value}</div>
        {s.note && <div className={styles.statNote}>{s.note}</div>}
      </div>
    ))}
  </div>
);

const pickNothing = (what: string) => (
  <EmptyState title={`Pick a ${what}`} description={`Choose a ${what} and a date range above to run the report.`} />
);

const ByProjectTab = () => {
  const { data: projects } = useProjects();
  const [projectId, setProjectId] = useState<string>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange());
  const { data: report, isLoading, isError, error } = useReportByProject(
    projectId, range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")
  );

  return (
    <>
      <Filters>
        <Select
          style={{ width: 240 }} placeholder="Project"
          options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
          onChange={setProjectId}
        />
        <RangePicker value={range} onChange={(v) => v && setRange(v as [Dayjs, Dayjs])} />
      </Filters>
      {!projectId ? pickNothing("project") : (
        <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
          {report && (
            <>
              <Stats items={[
                { label: "Total hours", value: hrs(report.totalMinutes) },
                { label: "Billable hours", value: hrs(report.billableMinutes), note: `${Math.round((report.billableMinutes / Math.max(report.totalMinutes, 1)) * 100)}% of logged time` },
                { label: "Total cost", value: `$${report.totalCost.toFixed(2)}` },
              ]} />
              <Table
                rowKey="userId" size="small" pagination={false} dataSource={report.byUser}
                columns={[
                  { title: "User", dataIndex: "name", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
                  { title: "Hours", dataIndex: "totalMinutes", width: 120, align: "right", render: (v) => <span className="q-num">{hrs(v)}</span> },
                  { title: "Cost", dataIndex: "cost", width: 140, align: "right", render: (v) => <span className="q-num">${v.toFixed(2)}</span> },
                ]}
              />
            </>
          )}
        </DisplayComponent>
      )}
    </>
  );
};

const ByClientTab = () => {
  const { data: clients } = useClients();
  const [clientId, setClientId] = useState<string>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange());
  const { data: report, isLoading, isError, error } = useReportByClient(
    clientId, range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")
  );

  return (
    <>
      <Filters>
        <Select
          style={{ width: 240 }} placeholder="Client"
          options={(clients || []).map((c: ClientProps) => ({ label: c.name, value: c._id }))}
          onChange={setClientId}
        />
        <RangePicker value={range} onChange={(v) => v && setRange(v as [Dayjs, Dayjs])} />
      </Filters>
      {!clientId ? pickNothing("client") : (
        <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
          {report && (
            <>
              <Stats items={[
                { label: "Total hours", value: hrs(report.totalMinutes) },
                { label: "Total cost", value: `$${report.totalCost.toFixed(2)}` },
              ]} />
              <Table
                rowKey="projectId" size="small" pagination={false} dataSource={report.byProject}
                columns={[
                  { title: "Project", dataIndex: "name", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
                  { title: "Hours", dataIndex: "totalMinutes", width: 120, align: "right", render: (v) => <span className="q-num">{hrs(v)}</span> },
                ]}
              />
            </>
          )}
        </DisplayComponent>
      )}
    </>
  );
};

const ByUserTab = () => {
  const { data: users } = useUsers();
  const [userId, setUserId] = useState<string>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange());
  const { data: report, isLoading, isError, error } = useReportByUser(
    userId, range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")
  );

  return (
    <>
      <Filters>
        <Select
          style={{ width: 240 }} placeholder="User"
          options={(users || []).map((u: SessionUser) => ({ label: u.name, value: u._id }))}
          onChange={setUserId}
        />
        <RangePicker value={range} onChange={(v) => v && setRange(v as [Dayjs, Dayjs])} />
      </Filters>
      {!userId ? pickNothing("user") : (
        <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
          {report && (
            <>
              <Stats items={[
                { label: "Total hours", value: hrs(report.totalMinutes) },
                { label: "Total cost", value: `$${report.totalCost.toFixed(2)}` },
              ]} />
              <Table
                rowKey={(r) => `${r.clientId}-${r.projectId}`} size="small" pagination={false}
                dataSource={report.byClientProject}
                columns={[
                  { title: "Client", dataIndex: "clientName" },
                  { title: "Project", dataIndex: "projectName", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
                  { title: "Hours", dataIndex: "totalMinutes", width: 120, align: "right", render: (v) => <span className="q-num">{hrs(v)}</span> },
                ]}
              />
            </>
          )}
        </DisplayComponent>
      )}
    </>
  );
};

const TimelineTab = () => {
  const { data: projects } = useProjects();
  const { data: clients } = useClients();
  const [scope, setScope] = useState<"project" | "client">("project");
  const [scopeId, setScopeId] = useState<string>();
  const [granularity, setGranularity] = useState<"day" | "week" | "month">("day");
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange());
  const { data: timeline, isLoading, isError, error } = useReportTimeline(
    scope, scopeId, range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD"), granularity
  );

  const peak = Math.max(...(timeline || []).map((t: any) => t.totalMinutes), 1);

  return (
    <>
      <Filters>
        <Segmented
          value={scope}
          onChange={(v) => { setScope(v as "project" | "client"); setScopeId(undefined); }}
          options={[{ label: "Project", value: "project" }, { label: "Client", value: "client" }]}
        />
        <Select
          style={{ width: 240 }}
          placeholder={scope === "project" ? "Project" : "Client"}
          options={(scope === "project" ? projects || [] : clients || []).map((item: ProjectProps | ClientProps) => ({ label: item.name, value: item._id }))}
          value={scopeId} onChange={setScopeId}
        />
        <Segmented
          value={granularity}
          onChange={(v) => setGranularity(v as "day" | "week" | "month")}
          options={[{ label: "Day", value: "day" }, { label: "Week", value: "week" }, { label: "Month", value: "month" }]}
        />
        <RangePicker value={range} onChange={(v) => v && setRange(v as [Dayjs, Dayjs])} />
      </Filters>
      {!scopeId ? pickNothing(scope) : (
      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table
          rowKey="bucket" size="small" dataSource={timeline || []}
          pagination={false}
          locale={{ emptyText: <EmptyState title="No time logged in this range" description="Widen the date range, or pick a different project." /> }}
          columns={[
            { title: "Bucket", dataIndex: "bucket", width: 160, render: (v) => <span className="q-num">{dayjs(v).format("D MMM YYYY")}</span> },
            { title: "Hours", dataIndex: "totalMinutes", width: 100, align: "right", render: (v) => <span className="q-num">{hrs(v)}</span> },
            {
              title: "",
              // inline bar: the distribution is the point of a timeline, and it
              // costs no new data to draw
              render: (_, r: any) => (
                <div style={{ height: 8, background: "var(--q-brand-700)", width: `${(r.totalMinutes / peak) * 100}%`, minWidth: 2 }} />
              ),
            },
          ]}
        />
      </DisplayComponent>
      )}
    </>
  );
};

const VelocityTab = () => {
  const { data: projects } = useProjects();
  const [projectId, setProjectId] = useState<string>();
  const { data: velocity, isLoading, isError, error } = useReportVelocity(projectId);
  const peak = Math.max(...(velocity || []).map((v: any) => v.completedPoints), 1);

  return (
    <>
      <Filters>
        <Select
          style={{ width: 240 }} placeholder="Project"
          options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
          onChange={setProjectId}
        />
      </Filters>
      {!projectId ? pickNothing("project") : (
        <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
          <Table
            rowKey="sprintId" size="small" dataSource={velocity || []} pagination={false}
            locale={{ emptyText: <EmptyState title="No completed sprints yet" description="Velocity appears once a sprint closes with points completed." /> }}
            columns={[
              { title: "Sprint", dataIndex: "sprintName", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
              { title: "Points", dataIndex: "completedPoints", width: 90, align: "right", render: (v) => <span className="q-num">{v}</span> },
              {
                title: "",
                render: (_, r: any) => (
                  <div style={{ height: 8, background: "var(--q-brand-700)", width: `${(r.completedPoints / peak) * 100}%`, minWidth: 2 }} />
                ),
              },
            ]}
          />
        </DisplayComponent>
      )}
    </>
  );
};

export const ReportsPage = () => {
  const [tab, setTab] = useState("by-project");
  return (
    <PageShell
      kind="list"
      kicker="Analytics"
      title="Reports"
      subtitle="Time, cost and delivery across every client and project."
      tabs={
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: "by-project", label: "By project" },
            { key: "by-client", label: "By client" },
            { key: "by-user", label: "By user" },
            { key: "timeline", label: "Timeline" },
            { key: "velocity", label: "Velocity" },
          ]}
        />
      }
    >
      {tab === "by-project" && <ByProjectTab />}
      {tab === "by-client" && <ByClientTab />}
      {tab === "by-user" && <ByUserTab />}
      {tab === "timeline" && <TimelineTab />}
      {tab === "velocity" && <VelocityTab />}
    </PageShell>
  );
};
